import { memo, Suspense, useRef } from "react";
import "./App.css";
import * as THREE from "three";
import puresky from "./assets/sunflowers_puresky_1k.hdr";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  MeshTransmissionMaterial,
  OrbitControls,
  Float,
  Environment,
  Center,
} from "@react-three/drei";
import { Bloom, EffectComposer } from "@react-three/postprocessing";
import { random_int, random_num } from "./common/utils";
import { fxpreview } from "./fxhash";
import { useState } from "react";
const scaling = 0.12;
const count = 64;

const Heart = ({ color, position, rotation, scale, index }) => {
  const heartRef = useRef();

  const x = 0;
  const y = 0;
  const heartShape = new THREE.Shape();

  heartShape.moveTo(x + 5, y + 5);
  heartShape.bezierCurveTo(x + 5, y + 5, x + 4, y, x, y);
  heartShape.bezierCurveTo(x - 6, y, x - 6, y + 7, x - 6, y + 7);
  heartShape.bezierCurveTo(x - 6, y + 11, x - 3, y + 15.4, x + 5, y + 19);
  heartShape.bezierCurveTo(x + 12, y + 15.4, x + 16, y + 11, x + 16, y + 7);
  heartShape.bezierCurveTo(x + 16, y + 7, x + 16, y, x + 10, y);
  heartShape.bezierCurveTo(x + 7, y, x + 5, y + 5, x + 5, y + 5);

  useFrame((state, delta) => {
    const wobble = Math.sin(state.clock.elapsedTime * 0.5) * 0.0002 * index;
    heartRef.current.position.x += wobble;
    if (index % 2 === 0) {
      heartRef.current.rotation.y += state.clock.elapsedTime * 0.000002 * index;
    } else {
      heartRef.current.rotation.y -= state.clock.elapsedTime * 0.000002 * index;
    }
    if (heartRef.current.position.y < 15) {
      heartRef.current.position.y += delta + index * 0.0001;
    } else {
      heartRef.current.position.y = -15;
    }
  });

  // ookJjCvDk3e2BVBa1moLH4iXEEUBTSfAwkZgpxoSMhoPU4DvNiX
  // oodFx3uLvR31fY2W6hYDduQo13J51B2KTcC66C6dAjwDy6tYnqV

  return (
    <Float>
      <mesh
        position={position}
        rotation={rotation}
        scale={scale}
        ref={heartRef}
      >
        <extrudeGeometry
          args={[
            heartShape,
            {
              steps: random_int(8, 16),
              depth: random_num(0.02, 4),
              bevelThickness: random_num(0.5, 6),
              bevelSegments: random_int(8, 16),
              bevelSize: random_num(2, 4),
              bevelOffset: random_num(-0.5, 0),
            },
          ]}
        />
        <MeshTransmissionMaterial
          resolution={32}
          distortion={20}
          color={color}
          thickness={3}
          roughness={0.5}
          metalness={0.1}
        />
        {/* <meshStandardMaterial
        color={color}
        roughness={0.2}
        metalness={0.9}
        toneMapped={false}
      /> */}
        {/* <meshToonMaterial color={color} /> */}
      </mesh>
    </Float>
  );
};

const MemoizedHeart = memo(Heart);

const Scene = () => {
  return (
    <Suspense fallback={null}>
      <Center>
        {[...Array(count)].map((_heart, i) => {
          const heartColor = `hsl(${random_num(290, 370)}, ${random_num(
            40,
            100
          )}%, ${random_num(40, 100)}%)`;
          const posX = random_num(-scaling * 128, scaling * 128);
          const posY = random_num(-scaling * 256, scaling * 256);
          const posZ = random_num(-scaling * 48, scaling * 48);
          const scale = random_num(scaling * 0.15, scaling * 1.5);
          return (
            <MemoizedHeart
              key={i}
              index={i}
              color={heartColor}
              position={[posX, posY, posZ]}
              rotation={[Math.PI, 0, 0]}
              scale={scale}
            />
          );
        })}
      </Center>
      <Environment files={puresky} />
    </Suspense>
  );
};

const MemoizedScene = memo(Scene);

const Experience = () => {
  return (
    <Canvas camera={{ position: [0, 2, 15], fov: 45 }}>
      <color
        attach="background"
        args={[
          `hsl(${random_num(310, 350)}, ${random_num(60, 100)}%, ${random_num(
            50,
            90
          )}%)`,
        ]}
      />
      <ambientLight intensity={0.5} />
      <spotLight position={[5, 5, -10]} angle={0.15} penumbra={1} />
      <pointLight position={[-10, -10, -10]} />
      <MemoizedScene />
      <OrbitControls
        makeDefault
        autoRotate
        autoRotateSpeed={0.3}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 2}
      />
      <EffectComposer>
        <Bloom luminanceThreshold={1} intensity={1} levels={10} mipmapBlur />
      </EffectComposer>
    </Canvas>
  );
};

export default Experience;
